import { BriefcaseBusinessIcon, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useFormatters } from '@/shared/i18n/format';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

import type { EntityType } from '../api';
import { ENTITY_ICONS } from '../model/icons';
import { formatMoney } from '../model/money';

/** Search page rendering of CRM documents: type, title and the stage/amount/company line. */
export function CrmSearchResult({ result, highlights }: { result: SearchResult; highlights?: SearchResultHighlights }) {
  const { t } = useTranslation('crm');
  const fmt = useFormatters();
  const type = result.entityType as EntityType;
  const Icon = (ENTITY_ICONS as Partial<Record<string, LucideIcon>>)[result.entityType] ?? BriefcaseBusinessIcon;
  const meta = result.meta ?? {};
  const amount = meta.amount && meta.currency ? formatMoney(Number(meta.amount), meta.currency, fmt.intlLocale) : null;
  const status = (type === 'quote' || type === 'invoice') && meta.status ? t(`documentStatus.${type}.${meta.status}`, { defaultValue: meta.status }) : (meta.stage ?? meta.status);
  const details = [type === 'item' ? (meta.typeTitle ?? t('entities.item')) : t(`entities.${type}`), status, amount, meta.company ?? meta.position].filter(Boolean);
  return (
    <div className="flex min-w-0 items-start gap-3" data-testid="crm-search-result">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <p className="truncate font-medium">{highlights?.title ?? result.title}</p>
        <p className="truncate text-xs text-muted-foreground">{details.join(' · ')}</p>
        {(highlights?.snippet ?? result.snippet) && <p className="line-clamp-2 text-xs text-muted-foreground">{highlights?.snippet ?? result.snippet}</p>}
      </div>
    </div>
  );
}
